import { useCallback, useMemo, useRef, useState, type FC } from 'react';
import { usePathname } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { AgentModelOption, PaidPlanTier } from '@tabbio/types';
import {
  AGENT_MODEL_AUTO,
  AGENT_MODEL_PROVIDER_ORDER,
} from '@tabbio/utils/agent-models';
import { MaterialSymbol } from '@/components/icons/MaterialSymbol';
import {
  WebMenu,
  WebMenuDivider,
  WebMenuTagOption,
} from '@/components/web/WebMenu';
import {
  useAgentModelPreference,
  useAgentModelReconciliation,
  useAgentModels,
} from '@/hooks/useAgentModels';
import { celebrateModelPick } from '@/lib/composer-celebration';
import { useSubscriptionState } from '@/providers/useSubscriptionState';
import { useUpgradeModal } from '@/providers/UpgradeModalProvider';
import { createChatUpgradeContext } from '@/lib/monetization-context';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Model switcher for the web composers: the model's name and a chevron.
 *
 * The menu is one row per model: the name, a tag on the right and a check on
 * the one in use, under a single gliding highlight. The tag is the plan a
 * model belongs to: a badge while that plan is still locked, plain text once
 * it is yours. Picking a Pro plan model (the flagship tier) plays the
 * composer's one-shot sweep (`composer-celebration.ts`).
 *
 * `header` is the phone layout's trigger in the chat header.
 */
export const ModelPicker: FC<{
  header?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}> = ({ header, open: controlledOpen, onOpenChange }) => {
  const { t } = useTranslation('common');
  const { data } = useAgentModels();
  const { selected, select } = useAgentModelPreference();
  const { snapshot } = useSubscriptionState();
  const upgradeModal = useUpgradeModal();
  const pathname = usePathname();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback((next: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  }, [controlledOpen, onOpenChange]);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useAgentModelReconciliation();

  const models = useMemo(() => data?.models ?? [], [data]);

  // Provider order still drives the sequence so a vendor's models stay
  // adjacent; it just no longer draws a heading.
  const orderedModels = useMemo(
    () =>
      AGENT_MODEL_PROVIDER_ORDER.flatMap((provider) =>
        models.filter(
          (model) =>
            model.provider === provider && model.minimumTier !== 'free',
        ),
      ),
    [models],
  );

  const selectedEntry =
    selected === AGENT_MODEL_AUTO
      ? undefined
      : models.find(
          (model) => model.id === selected && model.minimumTier !== 'free',
        );
  const triggerLabel = selectedEntry
    ? selectedEntry.displayName
    : t('chat.model.auto.title', { defaultValue: 'Auto' });

  const openUpgrade = useCallback(
    (recommendedTier: PaidPlanTier) => {
      upgradeModal.open(
        createChatUpgradeContext({
          entryPoint: 'chat.model_picker',
          planSet: snapshot?.planSet,
          recommendedTier,
          returnTo: pathname,
        }),
      );
    },
    [pathname, snapshot?.planSet, upgradeModal],
  );

  const handleSelect = useCallback(
    (model: AgentModelOption) => {
      // A locked row is a paywall entry point, not a selection: leave the
      // current pick alone and let the upgrade modal take over, opened on the
      // plan that actually unlocks this row.
      if (model.locked) {
        openUpgrade(model.minimumTier === 'pro' ? 'pro' : 'plus');
        return;
      }
      select(model.id);
      celebrateModelPick(model, { reducedMotion: prefersReducedMotion() });
    },
    [openUpgrade, select],
  );

  // With no models to choose from (no provider key configured) the picker is
  // just a control that can't do anything, so it is not rendered.
  if (!models.length) return null;

  const planWord = (model: AgentModelOption) =>
    model.minimumTier === 'pro'
      ? t('chat.model.locked.pro', { defaultValue: 'Pro' })
      : t('chat.model.locked.plus', { defaultValue: 'Plus' });
  const label = `${t('chat.model.label', { defaultValue: 'Model' })}: ${triggerLabel}`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={
          header ? 'tabbio-model-chip tabbio-phone-model' : 'tabbio-composer-model'
        }
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
      >
        <span
          className={
            header ? 'tabbio-model-chip-label' : 'tabbio-composer-model-label'
          }
        >
          {triggerLabel}
        </span>
        <MaterialSymbol
          name="expand_more"
          className={
            header ? 'tabbio-model-chip-caret' : 'tabbio-composer-model-caret'
          }
        />
      </button>

      <WebMenu
        open={open}
        onClose={() => setOpen(false)}
        anchor={triggerRef.current}
        placement="top-start"
        minWidth={240}
        mobileSheet
        glide
      >
        <div className="tabbio-phone-model-sheet-header">
          <span>{t('chat.model.label', { defaultValue: 'Model' })}</span>
          <button type="button" aria-label={t('actions.close')} onClick={() => setOpen(false)}>
            <MaterialSymbol name="close" size={20} />
          </button>
        </div>
        <WebMenuTagOption
          title={t('chat.model.auto.title', { defaultValue: 'Auto' })}
          description={t('chat.model.auto.description', {
            defaultValue: 'Recommended model for your plan',
          })}
          tooltip={t('chat.model.auto.description', {
            defaultValue: 'Recommended model for your plan',
          })}
          checked={selected === AGENT_MODEL_AUTO}
          onSelect={() => select(AGENT_MODEL_AUTO)}
        />

        <WebMenuDivider />

        {/* One flat list, no provider headings: catalog order keeps a
            vendor's models adjacent, and each row's description (speed and
            depth, which is how anyone picks here) is its hover text. */}
        {orderedModels.map((model) => (
          <WebMenuTagOption
            key={model.id}
            title={model.displayName}
            description={t(`chat.model.options.${model.slug}.description`)}
            tooltip={t(`chat.model.options.${model.slug}.description`)}
            checked={!model.locked && selected === model.id}
            badge={model.locked ? planWord(model) : undefined}
            tag={model.locked ? undefined : planWord(model)}
            newBadge={
              model.isNew
                ? t('chat.model.newBadge', { defaultValue: 'New' })
                : undefined
            }
            onSelect={() => handleSelect(model)}
          />
        ))}
      </WebMenu>
    </>
  );
};
